"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type ConfigSegment,
  configExceptions,
  configSegments,
  configText,
  ionConfig,
  isPredicted,
  nobleCore,
  orbitalBoxes,
  outerShell,
  readSpecies,
  type ShellOrder,
  type Subshell,
  sortShells,
  subshellLabel,
  totalElectrons,
  unpairedElectrons,
} from "@/lib/tool-kit/chem-reactions/electron-config";

/** English strings. A translated content file overrides these through `ui`. */
export const ELECTRON_CONFIGURATION_CALCULATOR_STRINGS = {
  speciesLabel: "Element or ion",
  speciesHint:
    "A symbol (Fe), a name (iron) or an atomic number (26). A charge can follow: Fe3+, Cl-, O2-.",
  speciesPlaceholder: "Fe",
  chargeLabel: "Charge (optional)",
  chargeHint: "Use this instead of writing the charge: 3, +3 or -1.",
  tryLabel: "Try an example",
  orderLabel: "Write the subshells",
  orderShell: "By shell: 3d before 4s (as NIST prints them)",
  orderFilling: "In filling order: 4s before 3d",
  outerOnly: "Orbital diagram: show only subshells outside the noble-gas core",
  results: "Results",
  enter: "Enter an element symbol, name or atomic number.",
  errors: {
    unknown:
      "That is not an element symbol, name or atomic number from 1 to 118.",
    badCharge: "The charge must be a whole number such as 2, +3 or -1.",
    tooManyRemoved: "That charge removes more electrons than the atom has.",
    tooManyAdded:
      "Anions beyond 4− are not shown; they do not form stable ions in practice.",
  },
  species: "Species",
  atomicNumber: "Atomic number",
  electrons: "Electrons",
  unpaired: "Unpaired electrons",
  magnetism: "Magnetism",
  paramagnetic: "Paramagnetic",
  diamagnetic: "Diamagnetic",
  outerShell: "Outer shell",
  outerShellValue: "n = {n}, {electrons} electrons",
  fullHeading: "Full configuration",
  nobleHeading: "Noble-gas configuration",
  diagramHeading: "Orbital diagram",
  diagramNote:
    "One box per orbital, filled by Hund's rule: electrons spread over a subshell's orbitals with parallel spins before any pair up.",
  subshellAria: "{subshell}: {electrons}, {orbitals}, {unpaired} unpaired",
  electronCount: "one: {count} electron|other: {count} electrons",
  orbitalCount: "one: {count} orbital|other: {count} orbitals",
  noElectrons: "This ion has no electrons, so there is nothing to show.",
  exceptionNote:
    "{symbol} is an exception to the filling order. The Madelung (Aufbau) rule predicts {predicted}, but the measured ground state is {actual}. In these atoms the energies of neighbouring subshells are so close that electron repulsion and exchange energy decide, and a half-filled or filled d subshell (as in Cr and Cu) often results.",
  exceptionIon:
    "The ion is built from the measured ground state of the neutral atom, {actual}.",
  predictedNote:
    "Elements 109–118 are not in NIST's table, because only a few atoms of each have been made. This is the Madelung-rule prediction; relativistic calculations suggest some of these elements differ.",
  cationNote:
    "Electrons are removed from the outermost subshells first: the highest n, then the highest l, valence subshells before the noble-gas core. So a transition metal loses 4s before 3d. Real ions occasionally differ, so check NIST's Atomic Spectra Database for spectroscopic ground states.",
  anionNote:
    "Extra electrons go into the next subshell in the Madelung filling order that has room.",
  copy: "Copy configurations",
  copied: "Copied!",
  copyText: "{species}: {full}; {noble}",
} as const;

type Strings = typeof ELECTRON_CONFIGURATION_CALCULATOR_STRINGS;

const EXAMPLES = ["Fe", "Cr", "Cu", "Fe3+", "Cl-", "Gd3+", "Au", "U"] as const;

function ConfigView({ segments }: { segments: readonly ConfigSegment[] }) {
  return (
    <span className="font-mono">
      {segments.map((segment, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: positional segments of one configuration
        <span key={index}>
          {index > 0 ? " " : null}
          {segment.text}
          {segment.sup ? <sup>{segment.sup}</sup> : null}
        </span>
      ))}
    </span>
  );
}

function Arrows({ count }: { count: 0 | 1 | 2 }) {
  if (count === 0) {
    return null;
  }
  return <span aria-hidden="true">{count === 2 ? "↑↓" : "↑"}</span>;
}

function Diagram({
  shells,
  order,
  strings,
}: {
  shells: readonly Subshell[];
  order: ShellOrder;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const sorted = sortShells(shells, order);
  return (
    <ul className="space-y-2">
      {sorted.map((shell) => {
        const boxes = orbitalBoxes(shell);
        const unpaired = boxes.filter((box) => box.electrons === 1).length;
        const label = subshellLabel(shell);
        return (
          <li
            aria-label={t(strings.subshellAria, {
              subshell: label,
              electrons: tPlural(
                strings.electronCount,
                locale,
                shell.electrons,
                {
                  count: fmt(shell.electrons),
                }
              ),
              orbitals: tPlural(strings.orbitalCount, locale, boxes.length, {
                count: fmt(boxes.length),
              }),
              unpaired: fmt(unpaired),
            })}
            className="flex items-center gap-3"
            key={label}
          >
            <span
              aria-hidden="true"
              className="w-8 font-mono text-foreground text-sm"
            >
              {label}
            </span>
            <span aria-hidden="true" className="flex gap-1">
              {boxes.map((box, index) => (
                <span
                  className="flex h-9 w-9 items-center justify-center border border-border bg-card text-foreground text-sm tracking-tighter"
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional orbitals of one subshell
                  key={index}
                >
                  <Arrows count={box.electrons} />
                </span>
              ))}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Notes({
  z,
  charge,
  symbol,
  strings,
  order,
}: {
  z: number;
  charge: number;
  symbol: string;
  strings: Strings;
  order: ShellOrder;
}) {
  const exception = configExceptions().find((entry) => entry.z === z);
  const predicted = isPredicted(z);
  return (
    <>
      {exception && charge === 0 ? (
        <Notice>
          {t(strings.exceptionNote, {
            symbol,
            predicted: configText(exception.predicted, "noble", order),
            actual: configText(exception.actual, "noble", order),
          })}
        </Notice>
      ) : null}
      {exception && charge !== 0 ? (
        <Notice>
          {t(strings.exceptionIon, {
            actual: configText(exception.actual, "noble", order),
          })}
        </Notice>
      ) : null}
      {predicted ? <Notice>{strings.predictedNote}</Notice> : null}
      {charge > 0 ? <Notice>{strings.cationNote}</Notice> : null}
      {charge < 0 ? <Notice>{strings.anionNote}</Notice> : null}
    </>
  );
}

function Answer({
  text,
  chargeText,
  order,
  outerOnly,
  strings,
}: {
  text: string;
  chargeText: string;
  order: ShellOrder;
  outerOnly: boolean;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const species = readSpecies(text, chargeText);
  if (!species.ok) {
    return species.reason === "empty" ? (
      <Notice>{strings.enter}</Notice>
    ) : (
      <ErrorNotice>{strings.errors[species.reason]}</ErrorNotice>
    );
  }
  const ion = ionConfig(species.z, species.charge);
  if (!ion.ok) {
    return <ErrorNotice>{strings.errors[ion.reason]}</ErrorNotice>;
  }
  const config = ion.config;
  if (config.length === 0) {
    return <Notice>{strings.noElectrons}</Notice>;
  }
  const unpaired = unpairedElectrons(config);
  const outer = outerShell(config);
  const core = nobleCore(config);
  const diagramShells =
    outerOnly && core
      ? config.filter(
          (s) => !core.shells.some((c) => c.n === s.n && c.l === s.l)
        )
      : config;
  const chargeLabel =
    species.charge === 0
      ? ""
      : `${species.charge > 0 ? "+" : "−"}${Math.abs(species.charge) === 1 ? "" : Math.abs(species.charge)}`;
  const speciesLabel = `${species.symbol}${chargeLabel}`;
  const fullText = configText(config, "full", order);
  const nobleText = configText(config, "noble", order);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.species}
          value={
            <>
              {species.symbol}
              {chargeLabel === "" ? null : <sup>{chargeLabel}</sup>}
            </>
          }
        />
        <StatCard label={strings.atomicNumber} value={fmt(species.z)} />
        <StatCard
          label={strings.electrons}
          value={fmt(totalElectrons(config))}
        />
        <StatCard label={strings.unpaired} value={fmt(unpaired)} />
        <StatCard
          label={strings.magnetism}
          value={unpaired > 0 ? strings.paramagnetic : strings.diamagnetic}
        />
        <StatCard
          label={strings.outerShell}
          value={t(strings.outerShellValue, {
            n: fmt(outer.n),
            electrons: fmt(outer.electrons),
          })}
        />
      </StatGrid>
      <div className={RESULT_CARD}>
        <p className="font-medium text-muted-foreground text-sm">
          {strings.fullHeading}
        </p>
        <p className="mt-2 text-foreground text-xl">
          <ConfigView segments={configSegments(config, "full", order)} />
        </p>
        <p className="mt-4 font-medium text-muted-foreground text-sm">
          {strings.nobleHeading}
        </p>
        <p className="mt-2 text-foreground text-xl">
          <ConfigView segments={configSegments(config, "noble", order)} />
        </p>
      </div>
      <Notes
        charge={species.charge}
        order={order}
        strings={strings}
        symbol={species.symbol}
        z={species.z}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          species: speciesLabel,
          full: fullText,
          noble: nobleText,
        })}
      />
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.diagramHeading}</h3>
        <Diagram order={order} shells={diagramShells} strings={strings} />
        <p className="text-muted-foreground text-xs">{strings.diagramNote}</p>
      </div>
    </>
  );
}

export default function ElectronConfigurationCalculatorWidget() {
  const strings = useToolUi(ELECTRON_CONFIGURATION_CALCULATOR_STRINGS);
  const [text, setText] = useState("Fe");
  const [charge, setCharge] = useState("");
  const [order, setOrder] = useState<ShellOrder>("shell");
  const [outerOnly, setOuterOnly] = useState(false);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.speciesHint}
          id="ecc-species"
          label={strings.speciesLabel}
          mono
          onChange={setText}
          placeholder={strings.speciesPlaceholder}
          value={text}
        />
        <TextField
          hint={strings.chargeHint}
          id="ecc-charge"
          label={strings.chargeLabel}
          numeric
          onChange={setCharge}
          value={charge}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {EXAMPLES.map((example) => (
          <button
            className={BUTTON_SMALL}
            key={example}
            onClick={() => {
              setText(example);
              setCharge("");
            }}
            type="button"
          >
            {example}
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="ecc-order"
          label={strings.orderLabel}
          onChange={setOrder}
          options={[
            { value: "shell", label: strings.orderShell },
            { value: "filling", label: strings.orderFilling },
          ]}
          value={order}
        />
        <div className="flex items-end pb-2">
          <Checkbox
            checked={outerOnly}
            label={strings.outerOnly}
            onChange={setOuterOnly}
          />
        </div>
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Answer
          chargeText={charge}
          order={order}
          outerOnly={outerOnly}
          strings={strings}
          text={text}
        />
      </section>
    </>
  );
}
